import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClipboardList, ClipboardCheck } from "lucide-react";
import { NAV_GROUPS } from "@/config/constants";
import { useFeatureFlags } from "@/shared/hooks/useFeatureFlags";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/shared/components/ui/command";

/**
 * Href-to-i18n key mapping — same as Sidebar.
 */
const NAV_I18N_KEYS: Record<string, string> = {
  "/":                    "nav.dashboard",
  "/locations":           "nav.locations",
  "/assets":              "nav.assets",
  "/work-orders":         "nav.workOrders",
  "/pm-schedules":        "nav.pmSchedules",
  "/checklists":          "nav.checklists",
  "/labor-tracking":      "nav.laborTracking",
  "/inventory":           "nav.inventory",
  "/vendors":             "nav.vendors",
  "/incidents":           "nav.incidents",
  "/meter-readings":      "nav.meterReadings",
  "/templates":           "nav.templates",
  "/inspections":         "nav.inspections",
  "/actions":             "nav.actions",
  "/inspection-schedules":"nav.inspectionSchedules",
  "/reports":             "nav.reports",
  "/analytics":           "nav.analytics",
  "/analytics/work-orders": "nav.analyticsWorkOrders",
  "/analytics/assets":      "nav.analyticsAssets",
  "/analytics/preventive":  "nav.analyticsPreventive",
  "/analytics/team":        "nav.analyticsTeam",
  "/analytics/safety":      "nav.analyticsSafety",
  "/users":               "nav.users",
  "/audit-log":           "nav.auditLog",
  "/custom-fields":       "nav.customFields",
  "/escalation-rules":    "nav.escalationRules",
  "/settings":            "nav.settings",
};

/**
 * Quick action definitions for the command palette.
 */
const QUICK_ACTIONS = [
  {
    labelKey: "commandPalette.createWorkOrder",
    href: "/work-orders/new",
    icon: ClipboardList,
  },
  {
    labelKey: "commandPalette.newInspection",
    href: "/inspections",
    icon: ClipboardCheck,
  },
];

/**
 * Global command palette component.
 *
 * Opens on Cmd+K (Mac) or Ctrl+K (Windows/Linux).
 * Provides two groups:
 *   - Navigation: all sidebar nav items flattened into one group
 *   - Quick Actions: shortcuts for common create flows
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const featureFlags = useFeatureFlags();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (href: string) => {
    navigate(href);
    setOpen(false);
  };

  // Flatten all NAV_GROUPS into a single list of items
  const allNavItems = NAV_GROUPS.flatMap((group) => group.items).filter((item) => {
    if (!item.featureFlag) return true;
    return featureFlags?.[item.featureFlag] !== false;
  });

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={t("commandPalette.placeholder", "Type a command or search...")} />
      <CommandList>
        <CommandEmpty>{t("commandPalette.empty", "No results found.")}</CommandEmpty>

        <CommandGroup heading={t("commandPalette.navigation", "Navigation")}>
          {allNavItems.map((item) => {
            const Icon = item.icon;
            const i18nKey = NAV_I18N_KEYS[item.href];
            const label = i18nKey ? t(i18nKey) : item.label;
            return (
              <CommandItem
                key={item.href}
                value={label}
                onSelect={() => handleSelect(item.href)}
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t("commandPalette.quickActions", "Quick Actions")}>
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            const label = t(action.labelKey);
            return (
              <CommandItem
                key={action.href}
                value={label}
                onSelect={() => handleSelect(action.href)}
              >
                <Icon className="mr-2 h-4 w-4" />
                <span>{label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
